import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { 
  Wrench, Search, Trash2, Loader2, AlertTriangle, 
  CheckCircle, Users, Database, Shield, Clock, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const ManutencaoPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [cleanResult, setCleanResult] = useState(null);
  
  // Estados para migração de marcações
  const [migracaoLoading, setMigracaoLoading] = useState(false);
  const [migracaoPreview, setMigracaoPreview] = useState(null);
  const [migracaoConfirmOpen, setMigracaoConfirmOpen] = useState(false);
  const [migracaoExecutando, setMigracaoExecutando] = useState(false);
  const [migracaoResultado, setMigracaoResultado] = useState(null);

  const handlePreview = async () => {
    setLoading(true);
    setPreviewData(null);
    setCleanResult(null);
    
    try {
      const response = await api.get('/admin/empregados-teste/preview');
      setPreviewData(response.data);
      
      if (response.data.total_encontrados === 0) {
        toast.info('Nenhum empregado de teste encontrado na base de dados');
      } else {
        toast.success(`Encontrados ${response.data.total_encontrados} empregado(s) de teste`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao buscar empregados de teste');
    } finally {
      setLoading(false);
    }
  };

  const handleCleanConfirm = async () => {
    setCleaning(true);
    
    try {
      const response = await api.delete('/admin/empregados-teste/limpar?confirmar=true');
      setCleanResult(response.data);
      setPreviewData(null);
      toast.success(response.data.message);
      setConfirmDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao limpar empregados de teste');
    } finally {
      setCleaning(false);
    }
  };

  // Funções para migração de marcações sem slot
  const handleMigracaoPreview = async () => {
    setMigracaoLoading(true);
    setMigracaoPreview(null);
    setMigracaoResultado(null);
    
    try {
      const response = await api.post('/admin/migrar-marcacoes-sem-slot?simular=true');
      setMigracaoPreview(response.data);
      
      if (response.data.total_marcacoes_sem_slot === 0) {
        toast.info('Nenhuma marcação antiga para migrar');
      } else {
        toast.success(`Encontradas ${response.data.total_marcacoes_sem_slot} marcação(ões) para migrar`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao buscar marcações');
    } finally {
      setMigracaoLoading(false);
    }
  };

  const handleMigracaoExecutar = async () => {
    setMigracaoExecutando(true);
    
    try {
      const response = await api.post('/admin/migrar-marcacoes-sem-slot?simular=false');
      setMigracaoResultado(response.data);
      setMigracaoPreview(null);
      toast.success('Migração executada com sucesso!');
      setMigracaoConfirmOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao executar migração');
    } finally {
      setMigracaoExecutando(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="manutencao-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-primary font-['Outfit'] mb-1">Manutenção</h2>
          <p className="text-muted-foreground">Ferramentas de manutenção e limpeza de dados</p>
        </div>
      </div>

      {/* Card de Limpeza de Dados de Teste */}
      <Card className="border-slate-100">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Database className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <CardTitle className="text-lg font-['Outfit']">Limpeza de Dados de Teste</CardTitle>
              <CardDescription>
                Identifique e remova empregados de teste da base de dados
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Informações sobre critérios */}
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Critérios de Identificação
            </h4>
            <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
              <li>Empregados vinculados a empresas com ambiente "teste"</li>
              <li>Nome ou email contendo: teste, test, exemplo, sample, demo, fake, mock, dummy</li>
              <li>CPFs obviamente falsos (000.000.000-00, 111.111.111-11, etc.)</li>
            </ul>
          </div>

          {/* Botões de Ação */}
          <div className="flex gap-3">
            <Button 
              onClick={handlePreview} 
              disabled={loading}
              variant="outline"
              className="flex items-center gap-2"
              data-testid="btn-preview-teste"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              {loading ? 'Buscando...' : 'Identificar Empregados de Teste'}
            </Button>
            
            {previewData && previewData.total_encontrados > 0 && (
              <Button 
                onClick={() => setConfirmDialogOpen(true)}
                variant="destructive"
                className="flex items-center gap-2"
                data-testid="btn-limpar-teste"
              >
                <Trash2 className="w-4 h-4" />
                Limpar {previewData.total_encontrados} Empregado(s)
              </Button>
            )}
          </div>

          {/* Preview dos empregados de teste */}
          {previewData && previewData.total_encontrados > 0 && (
            <div className="mt-6">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                <h4 className="font-medium text-slate-900">
                  {previewData.total_encontrados} empregado(s) de teste encontrado(s)
                </h4>
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-96 overflow-y-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-white">
                      <TableRow>
                        <TableHead>Nome</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>CPF</TableHead>
                        <TableHead>Matrícula</TableHead>
                        <TableHead>Motivo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewData.empregados.map((emp) => (
                        <TableRow key={emp.id} className="bg-amber-50/50">
                          <TableCell className="font-medium">{emp.nome}</TableCell>
                          <TableCell className="text-sm">{emp.email || '-'}</TableCell>
                          <TableCell className="font-mono text-sm">{emp.cpf || '-'}</TableCell>
                          <TableCell className="text-sm">{emp.matricula || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
                              {emp.motivo}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}

          {/* Resultado da limpeza */}
          {cleanResult && (
            <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <h4 className="font-medium text-emerald-900">Limpeza Concluída</h4>
              </div>
              
              <div className="space-y-2 text-sm">
                <p className="text-emerald-800">{cleanResult.message}</p>
                
                {cleanResult.dados_removidos && (
                  <div className="mt-3 p-3 bg-white rounded border border-emerald-200">
                    <p className="font-medium text-slate-700 mb-2">Dados removidos:</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <div className="text-slate-600">
                        <span className="font-medium">{cleanResult.dados_removidos.empregados || 0}</span> empregados
                      </div>
                      <div className="text-slate-600">
                        <span className="font-medium">{cleanResult.dados_removidos.marcacoes || 0}</span> marcações
                      </div>
                      <div className="text-slate-600">
                        <span className="font-medium">{cleanResult.dados_removidos.jornadas || 0}</span> jornadas
                      </div>
                      <div className="text-slate-600">
                        <span className="font-medium">{cleanResult.dados_removidos.ocorrencias || 0}</span> ocorrências
                      </div>
                      <div className="text-slate-600">
                        <span className="font-medium">{cleanResult.dados_removidos.banco_horas || 0}</span> banco de horas
                      </div>
                      <div className="text-slate-600">
                        <span className="font-medium">{cleanResult.dados_removidos.ajustes_banco_horas || 0}</span> ajustes
                      </div>
                    </div>
                  </div>
                )}
                
                <p className="text-slate-500 text-xs mt-2">
                  Executado por: {cleanResult.executado_por} em {new Date(cleanResult.data_execucao).toLocaleString('pt-BR')}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Card de Migração de Marcações Sem Slot */}
      <Card className="border-slate-100">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-lg font-['Outfit']">Migração de Marcações Antigas</CardTitle>
              <CardDescription>
                Atribua slots (E1, S1, E2, S2...) às marcações inseridas antes da implementação do sistema de posição fixa
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Informações sobre o processo */}
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
              <Wrench className="w-4 h-4" />
              O que será feito
            </h4>
            <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
              <li>Busca marcações sem campo <code className="bg-slate-200 px-1 rounded">slot</code> definido</li>
              <li>Agrupa por empregado e data</li>
              <li>Ordena por horário (ordem cronológica)</li>
              <li><strong>Horários duplicados:</strong> mantém apenas um, exclui os demais</li>
              <li>Atribui slots: E1 → S1 → E2 → S2 → E3 → S3</li>
            </ul>
          </div>

          {/* Botões de Ação */}
          <div className="flex gap-3">
            <Button 
              onClick={handleMigracaoPreview} 
              disabled={migracaoLoading}
              variant="outline"
              className="flex items-center gap-2"
              data-testid="btn-preview-migracao"
            >
              {migracaoLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              {migracaoLoading ? 'Analisando...' : 'Simular Migração'}
            </Button>
            
            {migracaoPreview && migracaoPreview.total_marcacoes_sem_slot > 0 && (
              <Button 
                onClick={() => setMigracaoConfirmOpen(true)}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                data-testid="btn-executar-migracao"
              >
                <ArrowRight className="w-4 h-4" />
                Executar Migração
              </Button>
            )}
          </div>

          {/* Preview da migração */}
          {migracaoPreview && migracaoPreview.total_marcacoes_sem_slot > 0 && (
            <div className="mt-6">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-5 h-5 text-blue-500" />
                <h4 className="font-medium text-slate-900">
                  {migracaoPreview.total_marcacoes_sem_slot} marcação(ões) encontrada(s)
                </h4>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-700">{migracaoPreview.total_dias_processados}</p>
                  <p className="text-xs text-blue-600">Dias a processar</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-700">{migracaoPreview.total_migradas}</p>
                  <p className="text-xs text-green-600">Receberão slot</p>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-amber-700">{migracaoPreview.total_duplicadas_excluidas}</p>
                  <p className="text-xs text-amber-600">Duplicadas a excluir</p>
                </div>
                <div className="p-3 bg-slate-100 rounded-lg text-center">
                  <p className="text-2xl font-bold text-slate-700">{migracaoPreview.total_marcacoes_sem_slot}</p>
                  <p className="text-xs text-slate-600">Total sem slot</p>
                </div>
              </div>
              
              {migracaoPreview.detalhes && migracaoPreview.detalhes.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-64 overflow-y-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-white">
                        <TableRow>
                          <TableHead>Data</TableHead>
                          <TableHead>Marcações Migradas</TableHead>
                          <TableHead>Duplicadas</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {migracaoPreview.detalhes.map((item, idx) => (
                          <TableRow key={idx} className="bg-blue-50/30">
                            <TableCell className="font-medium font-mono text-sm">
                              {item.data}
                            </TableCell>
                            <TableCell className="text-sm">
                              {item.migradas.map((m, i) => (
                                <Badge key={i} variant="outline" className="mr-1 mb-1 text-green-600 border-green-300">
                                  {m.hora} → {m.slot}
                                </Badge>
                              ))}
                            </TableCell>
                            <TableCell className="text-sm">
                              {item.duplicadas.length > 0 ? (
                                item.duplicadas.map((d, i) => (
                                  <Badge key={i} variant="outline" className="mr-1 mb-1 text-amber-600 border-amber-300">
                                    {d.hora}
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {migracaoPreview.detalhes.length >= 50 && (
                    <div className="p-2 bg-slate-100 text-center text-xs text-slate-500">
                      Exibindo primeiros 50 registros
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Resultado da migração */}
          {migracaoResultado && (
            <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <h4 className="font-medium text-emerald-900">Migração Concluída</h4>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-700">{migracaoResultado.total_migradas}</p>
                  <p className="text-xs text-emerald-600">Marcações migradas</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-700">{migracaoResultado.total_duplicadas_excluidas}</p>
                  <p className="text-xs text-amber-600">Duplicadas excluídas</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-700">{migracaoResultado.total_dias_processados}</p>
                  <p className="text-xs text-slate-600">Dias processados</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de Confirmação - Limpeza de Teste */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Confirmar Limpeza de Dados de Teste
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Você está prestes a remover permanentemente:
              </p>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="font-semibold text-amber-900 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  {previewData?.total_encontrados || 0} empregado(s) de teste
                </p>
              </div>
              <p className="text-slate-600">
                Esta ação também removerá todos os dados relacionados:
              </p>
              <ul className="text-sm text-slate-500 list-disc list-inside space-y-1">
                <li>Marcações de ponto</li>
                <li>Jornadas processadas</li>
                <li>Ocorrências e justificativas</li>
                <li>Banco de horas e ajustes</li>
              </ul>
              <p className="text-destructive font-semibold">
                Esta ação NÃO pode ser desfeita!
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cleaning}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCleanConfirm}
              disabled={cleaning}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirmar-limpeza"
            >
              {cleaning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Limpando...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Sim, Limpar Dados de Teste
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de Confirmação - Migração de Marcações */}
      <AlertDialog open={migracaoConfirmOpen} onOpenChange={setMigracaoConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-blue-600 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Confirmar Migração de Marcações
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Você está prestes a migrar:
              </p>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                <p className="font-semibold text-blue-900">
                  {migracaoPreview?.total_migradas || 0} marcações receberão slot
                </p>
                <p className="text-amber-700">
                  {migracaoPreview?.total_duplicadas_excluidas || 0} marcações duplicadas serão excluídas
                </p>
              </div>
              <p className="text-slate-600 text-sm">
                As marcações duplicadas serão marcadas como "excluída" e podem ser consultadas no histórico se necessário.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={migracaoExecutando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleMigracaoExecutar}
              disabled={migracaoExecutando}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirmar-migracao"
            >
              {migracaoExecutando ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Migrando...
                </>
              ) : (
                <>
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Executar Migração
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ManutencaoPage;
